import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { tCell, Button, GenericTable, Dropdown, DateTimePicker, Dialog, Input } from "../utils";
import { useOrgs, usePlant, useThirdPartyLogs, useUserDetail, useUserPermissions } from "../../services/hooks";
import { usePlantContext } from "../../context/plantContext";
import { findDeepValue } from "../../services/functions";
import { buttonClasses } from "../utils/theme";
import { formatDate } from "../../services/functions";
import { Navigate } from "react-router-dom/dist";
import dayjs from "dayjs";
import "./ThirdPartyLogs.css";

const defaultFilter = {
	pagesize: 10,
	page_no: 1,
};

const today = dayjs();
const startDate = today.subtract(1, "day");
const endDate = today;

const ThirdPartyLogs = () => {
	const location = useLocation();
	const { userDetail, isSuperAdmin } = useUserDetail();
	const { userPermissions, isFetched } = useUserPermissions();
	const { plantId: contextPlantId } = usePlantContext();
	const filterRef = useRef(defaultFilter);
	const { page_no, pagesize } = filterRef.current;
	const { logs, count, tableHeader, isLoading, getThirdPartyLogs } = useThirdPartyLogs();
	const [error, setError] = useState({});
	const initialCallDone = useRef(false);

	const [filter, setFilter] = useState({
		startDate,
		endDate,
		orgId: isSuperAdmin ? "" : userDetail?.orgId || "",
		orgName: isSuperAdmin ? "" : userDetail?.orgName || "",
		plantId: !isSuperAdmin && contextPlantId ? contextPlantId : "",
		vehicleNumber: "",
		api_name: "",
	});

	useEffect(() => {
		if (!isSuperAdmin && userDetail?.orgId) {
			setFilter((o) => ({ ...o, orgId: userDetail.orgId, orgName: userDetail.orgName || "", plantId: contextPlantId || "" }));
		}
	}, [isSuperAdmin, userDetail, contextPlantId]);

	useEffect(() => {
		if (!location.state) return;
		const { orgId, orgName } = location.state;
		setFilter((o) => ({ ...o, orgId, orgName }));
	}, [location]);

	const { orgOptions } = useOrgs({}, { skip: !isSuperAdmin });
	const orgValue = orgOptions.find((_) => _.value == filter.orgId) || null;

	const { plantOptions, getPlantDetails, clearPlants } = usePlant();
	const plantValue = plantOptions.find((_) => _.value == filter.plantId) || null;

	useEffect(() => {
		if (filter.orgId) {
			getPlantDetails({ orgId: filter.orgId });
		} else {
			clearPlants();
		}
	}, [filter.orgId]);

	const handleFilterChange = (name, value) => {
		setFilter((o) => ({ ...o, [name]: value }));
	};

	useEffect(() => {
		const filters = [];
		if (filter.plantId) {
			filters.push({ path: "plantId", value: filter.plantId });
		}
		if (filter.vehicleNumber) {
			filters.push({ path: "vehicleNumber", value: filter.vehicleNumber });
		}
		if (filter.api_name) {
			filters.push({ path: "api_name", value: filter.api_name });
		}
		filterRef.current = {
			...filterRef.current,
			startDate: filter.startDate?.$d == "Invalid Date" ? new Date().toISOString() : filter.startDate.toISOString(),
			endDate: filter.endDate?.$d == "Invalid Date" ? new Date().toISOString() : filter.endDate.toISOString(),
			orgId: filter.orgId,
			// orgName: filter.orgName,
			filters,
		};
	}, [filter]);

	useEffect(() => {
		if (!isSuperAdmin && filter.orgId && !initialCallDone.current) {
			initialCallDone.current = true;
			getThirdPartyLogs(filterRef.current);
		}
	}, [filter.orgId, isSuperAdmin]);

	function handleGetLogs() {

		setError({});
		getThirdPartyLogs(filterRef.current);
	}

	const [showJsonView, setShowJsonView] = useState(false);
	const jsonData = useRef({});

	let header = ["S.No", ...(tableHeader || []).map((_) => _.label)];

	let rows = logs?.map((data, i) => {
		let row = [tCell((page_no - 1) * pagesize + i + 1)];

		for (let item of tableHeader || []) {
			let value = findDeepValue(data, item.name.split(".")) ?? "";

			if (value && item.type === "object") {
				row.push(
					tCell(
						`${Object.keys(value).length} Fields`,
						"var(--clr-primary)",
						"pointer",
						() => {
							setShowJsonView(true);
							jsonData.current = value;
						},
						i
					)
				);
			} else {
				row.push(tCell(item.type === "date" ? formatDate(value, item.format) : (value !== "" ? String(value) : " - ")));
			}
		}

		return row;
	});

	const onPageChange = (page_no) => {
		filterRef.current = { ...filterRef.current, page_no: page_no + 1 };
		getThirdPartyLogs(filterRef.current);
	};
	const onPageSizeChange = (e) => {
		const { value } = e.target;
		filterRef.current = { ...filterRef.current, pagesize: value, page_no: 1 };
		getThirdPartyLogs(filterRef.current);
	};

	if (isFetched && !isSuperAdmin && !userPermissions?.viewApiLogs?.value) return <Navigate to="/not-authorized" replace />;

	return (
		<div className="contentpanel">
			<div className="cust-row flex-algn-cent">
				<div className="cust-col-5">
					<h1 className="title">API Logs</h1>
				</div>
				<div className="cust-col-5 flex-jc-end">
					<Link to="/companies">
						<Button text="Go Back" style={buttonClasses.lynkitOrangeEmpty} />
					</Link>
				</div>
			</div>
			<hr className="bgg" />
			<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
				{isSuperAdmin && (
					<Dropdown
						label="Organization"
						name="orgId"
						value={orgValue}
						options={orgOptions}
						// required
						// error={error.type}
						onChange={(op, name) => {
							handleFilterChange("orgId", op?.value || "");
							handleFilterChange("orgName", op?.label || "");
							handleFilterChange("plantId", "");
						}}
					/>
				)}
				{filter.orgId && (
					<Dropdown
						label="Plant"
						name="plantId"
						value={plantValue}
						options={plantOptions}
						onChange={(op) => {
							handleFilterChange("plantId", op?.value || "");
						}}
					/>
				)}
				<Input
					label="Api Name"
					name="api_name"
					value={filter.api_name}
					onChange={(e) => handleFilterChange("api_name", e.target.value)}
				/>
				<Input
					label="Vehicle Number"
					name="vehicleNumber"
					value={filter.vehicleNumber}
					onChange={(e) => handleFilterChange("vehicleNumber", e.target.value)}
				/>
				<DateTimePicker
					label="From"
					value={filter.startDate}
					onChange={(value) => {
						handleFilterChange("startDate", value);
					}}
				/>
				<DateTimePicker
					label="To"
					value={filter.endDate}
					onChange={(value) => {
						handleFilterChange("endDate", value);
					}}
				/>
				{/* <DatePicker
					label="Date"
					name="date"
					value={filter.date?.$d == "Invalid Date" ? null : filter.date}
					maxDATE={tomorrow}
					onChange={(value) => {
						handleFilterChange("date", value);
					}}
					isRequired
				/> */}
				{/* <TimePicker label="Start Time" value={filter.startTime} onChange={(value) => handleFilterChange("startTime", value)} isRequired /> */}
				{/* <TimePicker label="End Time" value={filter.endTime} onChange={(value) => handleFilterChange("endTime", value)} isRequired /> */}
				<Button text={`Submit`} onClick={handleGetLogs} style={{ ...buttonClasses.lynkitOrangeFill, width: "fit-content", height: "fit-content" }} disabled={isLoading} />
			</div>
			<div className="main_content">
				<GenericTable header={header} rows={rows} pageCount={count} pageNo={page_no} limit={pagesize} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} isLoading={isLoading} />
			</div>

			{showJsonView ? (
				<Dialog title="JSON Viewer" size="xl" open={showJsonView} handleClose={() => setShowJsonView(false)} noBtn>
					{parseJson(jsonData.current, true)}
				</Dialog>
			) : null}
		</div>
	);
};

export default ThirdPartyLogs;

function parseJson(obj, collapsible) {
	let ui = [];
	for (let item in obj) {
		ui.push(handleJsonItem(item, obj[item], collapsible));
	}
	return <div className="json">{ui}</div>;
}

function handleJsonItem(key, value, collapsible) {
	let type = Array.isArray(value) ? "array" : typeof value;

	if (typeof value === "object") {
		let html = [];
		for (let item in value) {
			html.push(handleJsonItem(item, value[item], collapsible));
		}
		return JsonItemCollapsible(key, type, type, collapsible, html);
	}
	return JsonItem(key, type == "string" ? `"${value}"` : value, type);
}

function JsonItem(key, value, type) {
	// console.log("key", key, " ---- ", "value", value);
	return (
		<div className="json__item" key={key + value + type}>
			<div className="json__key">{key}</div>
			<div className={`json__value json__value--${type}`}>{value}</div>
		</div>
	);
}

function JsonItemCollapsible(key, value, type, collapsible = false, children) {
	return (
		<label className="json__item json__item--collapsible">
			<input type="checkbox" className="json__toggle" defaultChecked={collapsible} />
			<div className="json__key">{key}</div>
			<div className={`json__value json__value--${type}`}>{value}</div>
			{children}
		</label>
	);
}
